import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Toast.module.css";

/**
 * Toast — bottom-right, auto-dismiss at 4s, manual close, max stack of 3.
 *
 * Four intents matching the StatusChip colours:
 *   pending   → accent
 *   completed → money-in
 *   failed    → danger
 *   reversed  → tertiary
 *
 * Use with the hook: `const toast = useToast(); toast.push({ ... })`.
 * The provider mounts a single portal at document.body; keep it once
 * near the app root.
 */

export type ToastIntent = "pending" | "completed" | "failed" | "reversed";

export interface ToastPayload {
  id?: string;
  title: string;
  body?: string;
  intent?: ToastIntent;
  /** ms until auto-dismiss. Default 4000. Pass 0 to disable auto-dismiss. */
  timeout?: number;
}

interface ActiveToast extends Required<Omit<ToastPayload, "body">> {
  body?: string;
}

const MAX_STACK = 3;
const DEFAULT_TIMEOUT = 4000;

interface ToastContextValue {
  push: (payload: ToastPayload) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((payload: ToastPayload): string => {
    idRef.current += 1;
    const id = payload.id ?? `t-${idRef.current}`;
    const active: ActiveToast = {
      id,
      title: payload.title,
      body: payload.body,
      intent: payload.intent ?? "pending",
      timeout: payload.timeout ?? DEFAULT_TIMEOUT,
    };
    setToasts((prev) => [...prev.slice(-(MAX_STACK - 1)), active]);
    return id;
  }, []);

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

interface ToastStackProps {
  toasts: ActiveToast[];
  onDismiss: (id: string) => void;
}

function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className={styles.container} role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastItemProps {
  toast: ActiveToast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  useEffect(() => {
    if (toast.timeout <= 0) return;
    const timer = setTimeout(onDismiss, toast.timeout);
    return () => clearTimeout(timer);
  }, [toast.timeout, onDismiss]);

  return (
    <div
      className={styles.toast}
      role={toast.intent === "failed" ? "alert" : "status"}
      aria-live={toast.intent === "failed" ? "assertive" : "polite"}
    >
      <span aria-hidden className={`${styles.dot} ${styles[toast.intent]}`} />
      <div className={styles.text}>
        <p className={styles.title}>{toast.title}</p>
        {toast.body ? <p className={styles.body}>{toast.body}</p> : null}
      </div>
      <button
        type="button"
        className={styles.close}
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        Dismiss
      </button>
    </div>
  );
}
